import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { contentWrite, createPreviewPR } from "../../services/content";
import PublishBar from "../../components/admin/PublishBar";

const ContactAdmin: React.FC = () => {
  const [tr, setTr] = React.useState<{ subjects: string[] }>({ subjects: [] });
  const [en, setEn] = React.useState<{ subjects: string[] }>({ subjects: [] });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [newTr, setNewTr] = React.useState("");
  const [newEn, setNewEn] = React.useState("");
  const [publishMode, setPublishMode] = React.useState<"prod" | "preview">(
    "prod"
  );
  const [commitMsg, setCommitMsg] = React.useState("chore(contact): update");
  const [branchName, setBranchName] = React.useState("");
  const [prUrl, setPrUrl] = React.useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [rtr, ren] = await Promise.all([
        fetch("/data/pages/contact/tr.json", { cache: "no-store" }).then((r) =>
          r.json()
        ),
        fetch("/data/pages/contact/en.json", { cache: "no-store" }).then((r) =>
          r.json()
        ),
      ]);
      setTr(rtr || { subjects: [] });
      setEn(ren || { subjects: [] });
    } catch (e: any) {
      setError(e?.message || "Veri okunamadı");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setError("");
    try {
      if (
        (tr.subjects || []).some((s) => !s.trim()) ||
        (en.subjects || []).some((s) => !s.trim())
      ) {
        throw new Error("Boş konu başlığı olamaz");
      }
      if (
        publishMode === "preview" &&
        branchName &&
        !/^[a-zA-Z0-9/_-]+$/.test(branchName)
      ) {
        throw new Error("Geçersiz branch adı");
      }
      const branch =
        publishMode === "preview"
          ? branchName || `preview/contact-${Date.now()}`
          : undefined;
      await Promise.all([
        contentWrite(
          "data/pages/contact/tr.json",
          JSON.stringify(tr, null, 2),
          `${commitMsg} tr`,
          branch
        ),
        contentWrite(
          "data/pages/contact/en.json",
          JSON.stringify(en, null, 2),
          `${commitMsg} en`,
          branch
        ),
      ]);
      if (branch) {
        const pr = await createPreviewPR(branch, "Contact Page Update");
        setPrUrl(pr.url);
      }
      setSuccess("Kaydedildi");
    } catch (e: any) {
      setError(e?.message || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          İletişim Konuları
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          İpucu: TR ve EN konu listelerini ayrı ayrı yönetin; boş konu
          eklemeyin. Yayın çubuğu ile Prod/PR seçebilirsiniz.
        </Alert>

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <PublishBar
            commitMsg={commitMsg}
            setCommitMsg={setCommitMsg}
            publishMode={publishMode}
            setPublishMode={setPublishMode}
            branchName={branchName}
            setBranchName={setBranchName}
            branchPlaceholder={`preview/contact`}
          />
          <Button
            variant="contained"
            onClick={save}
            disabled={loading}
          >
            Değişiklikleri Yayına Al
          </Button>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Türkçe
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    label="Yeni konu"
                    value={newTr}
                    onChange={(e) => setNewTr(e.target.value)}
                    fullWidth
                  />
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => {
                      if (!newTr.trim()) return;
                      setTr((d) => ({
                        subjects: [...d.subjects, newTr.trim()],
                      }));
                      setNewTr("");
                    }}
                  >
                    Ekle
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {tr.subjects.map((s, i) => (
                    <Stack
                      key={`${s}-${i}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <TextField
                        fullWidth
                        value={s}
                        onChange={(e) =>
                          setTr((d) => ({
                            subjects: d.subjects.map((x, idx) =>
                              idx === i ? e.target.value : x
                            ),
                          }))
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() =>
                          setTr((d) => ({
                            subjects: d.subjects.filter((_, idx) => idx !== i),
                          }))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  English
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    label="New subject"
                    value={newEn}
                    onChange={(e) => setNewEn(e.target.value)}
                    fullWidth
                  />
                  <Button
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => {
                      if (!newEn.trim()) return;
                      setEn((d) => ({
                        subjects: [...d.subjects, newEn.trim()],
                      }));
                      setNewEn("");
                    }}
                  >
                    Add
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {en.subjects.map((s, i) => (
                    <Stack
                      key={`${s}-${i}`}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <TextField
                        fullWidth
                        value={s}
                        onChange={(e) =>
                          setEn((d) => ({
                            subjects: d.subjects.map((x, idx) =>
                              idx === i ? e.target.value : x
                            ),
                          }))
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() =>
                          setEn((d) => ({
                            subjects: d.subjects.filter((_, idx) => idx !== i),
                          }))
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={load} disabled={loading}>
            Yenile
          </Button>
        </Stack>

        <Snackbar
          open={!!error}
          onClose={() => setError("")}
          autoHideDuration={5000}
        >
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar
          open={!!success}
          onClose={() => setSuccess("")}
          autoHideDuration={2500}
        >
          <Alert severity="success" onClose={() => setSuccess("")}>
            {success}
            {prUrl && (
              <Button
                component="a"
                href={prUrl}
                target="_blank"
                size="small"
                sx={{ ml: 1 }}
              >
                PR'ı Aç
              </Button>
            )}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ContactAdmin;
